import React from 'react';
import { Card, CardContent, Typography, Box, useTheme, Avatar, Divider, CircularProgress } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import RemoveIcon from '@mui/icons-material/Remove';

interface KPICardProps {
    title: string;
    value: string | number;
    subtitle?: string;
    trend?: 'up' | 'down' | 'neutral';
    trendValue?: string;
    color?: 'primary' | 'success' | 'error' | 'warning' | 'info';
    icon?: React.ReactNode;
    loading?: boolean;
}

const KPICard: React.FC<KPICardProps> = ({
    title,
    value,
    subtitle,
    trend,
    trendValue,
    color = 'primary',
    icon,
    loading = false,
}) => {
    const theme = useTheme();

    const getTrendIcon = () => {
        switch (trend) {
            case 'up':
                return <TrendingUpIcon sx={{ fontSize: 14 }} />;
            case 'down':
                return <TrendingDownIcon sx={{ fontSize: 14 }} />;
            case 'neutral':
                return <RemoveIcon sx={{ fontSize: 14 }} />;
            default:
                return null;
        }
    };

    const getTrendColor = () => {
        switch (trend) {
            case 'up':
                return theme.palette.success.main;
            case 'down':
                return theme.palette.error.main;
            default:
                return theme.palette.text.secondary;
        }
    };

    const getCardColor = () => {
        switch (color) {
            case 'success':
                return theme.palette.success.main;
            case 'error':
                return theme.palette.error.main;
            case 'warning':
                return theme.palette.warning.main;
            case 'info':
                return theme.palette.info.main;
            default:
                return theme.palette.primary.main;
        }
    };

    return (
        <Card
            sx={{
                height: '100%',
                transition: 'all 0.3s ease',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.palette.mode === 'dark'
                        ? '0 12px 32px rgba(0, 0, 0, 0.8)'
                        : theme.shadows[12],
                },
                overflow: 'visible', // Allow icon to float above card
            }}
        >
            <CardContent sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, px: 2 }}>
                {/* Icon Box - Floating above card */}
                {icon && (
                    <Avatar
                        sx={{
                            width: 64,
                            height: 64,
                            bgcolor: getCardColor(),
                            borderRadius: 3,
                            boxShadow: `0 4px 12px ${getCardColor()}40`,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            mt: -3,
                        }}
                    >
                        <Box sx={{ fontSize: '1.5rem', color: '#fff' }}>
                            {icon}
                        </Box>
                    </Avatar>
                )}

                {/* Content on the right - Right aligned */}
                <Box sx={{ textAlign: 'right', lineHeight: 1.25 }}>
                    {/* Title */}
                    <Typography
                        variant="button"
                        color="text.secondary"
                        sx={{
                            fontWeight: 'light',
                            fontSize: '0.875rem',
                        }}
                    >
                        {title}
                    </Typography>

                    {/* Value */}
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', py: 1 }}>
                            <CircularProgress size={32} />
                        </Box>
                    ) : (
                        <Typography
                            variant="h4"
                            component="div"
                            sx={{
                                fontWeight: 700,
                                color: theme.palette.text.primary,
                            }}
                        >
                            {typeof value === 'number' ? value.toLocaleString() : value}
                        </Typography>
                    )}

                    {/* Subtitle */}
                    {subtitle && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 0.5 }}
                        >
                            {subtitle}
                        </Typography>
                    )}
                </Box>
            </CardContent>

            {/* Trend Indicator - Outside CardContent with Divider */}
            {trend && trendValue && (
                <>
                    <Divider />
                    <Box sx={{ pb: 2, px: 2 }}>
                        <Typography
                            component="p"
                            variant="button"
                            color="text.secondary"
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                        >
                            <Typography
                                component="span"
                                variant="button"
                                sx={{
                                    fontWeight: 'bold',
                                    color: getTrendColor(),
                                }}
                            >
                                {getTrendIcon()}
                                {trendValue}
                            </Typography>
                        </Typography>
                    </Box>
                </>
            )}
        </Card>
    );
};

export default KPICard;
